import { CONTACT_LANGUAGES, CONTACT_TOPICS, type ContactLanguage, type ContactTopic } from "@kmstories/shared";
import { useState, type FormEvent } from "react";
import { useT } from "../i18n/LocaleProvider.js";
import { api, ApiError } from "../lib/api.js";
import { useDocumentTitle } from "../lib/useDocumentTitle.js";
import { useFormToken } from "../lib/useFormToken.js";

export function ContactPage() {
  const { m, locale } = useT();
  useDocumentTitle(m.meta.pages.contact);
  const { getToken } = useFormToken("contact");
  const [topic, setTopic] = useState<ContactTopic>("casting");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [languages, setLanguages] = useState<ContactLanguage[]>([]);
  const [languagesOther, setLanguagesOther] = useState("");
  const [location, setLocation] = useState("");
  const [links, setLinks] = useState<string[]>([""]);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [message, setMessage] = useState("");
  const [consentStore, setConsentStore] = useState(false);
  const [keepOnFile, setKeepOnFile] = useState(false);
  const [state, setState] = useState<"idle" | "busy" | "done">("idle");
  const [error, setError] = useState<string | null>(null);
  const casting = topic === "casting";

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!consentStore || (casting && !ageConfirmed)) {
      setError(m.share.errors.consentRequired);
      return;
    }
    const form = e.currentTarget as HTMLFormElement;
    setState("busy");
    setError(null);
    try {
      const formToken = await getToken();
      const hp = form.elements.namedItem("fax") as HTMLInputElement | null;
      await api.post("/api/contact", {
        topic,
        name: name.trim(),
        email,
        phone: phone.trim() || undefined,
        languages: casting && languages.length ? languages : undefined,
        languagesOther: casting && languages.includes("other") && languagesOther.trim() ? languagesOther.trim() : undefined,
        location: casting && location.trim() ? location.trim() : undefined,
        links: casting ? links.map((l) => l.trim()).filter(Boolean) : undefined,
        ageConfirmed: casting ? ageConfirmed : undefined,
        message: message.trim(),
        consentStore: true,
        keepOnFile: casting ? keepOnFile : false,
        locale,
        formToken,
        fax: hp?.value ?? "",
      });
      setState("done");
    } catch (err) {
      setState("idle");
      if (err instanceof ApiError && err.status === 400) setError(m.share.errors.invalid);
      else if (err instanceof ApiError && err.status === 429) setError(m.share.errors.rateLimited);
      else setError(m.common.error);
    }
  }

  return (
    <div className="container container--narrow">
      <div className="page-head">
        <h1>{m.contact.heading}</h1>
        <p className="page-head__lede">{m.contact.intro}</p>
      </div>

      {state === "done" ? (
        <p className="notice" role="status">
          {m.contact.done}
        </p>
      ) : (
        <form className="form card" onSubmit={submit} noValidate>
          <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="field__label">{m.contact.topicLabel}</legend>
            <div className="radio-cards">
              {CONTACT_TOPICS.map((t) => (
                <label key={t} className="radio-card">
                  <input type="radio" name="topic" value={t} checked={topic === t} onChange={() => setTopic(t)} />
                  <span className="radio-card__title">{m.contact.topics[t]}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="grid-2">
            <div className="field">
              <label className="field__label" htmlFor="c-name">
                {m.contact.nameLabel}
              </label>
              <input id="c-name" className="input" dir="auto" autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="c-email">
                {m.contact.emailLabel}
              </label>
              <input id="c-email" className="input" type="email" inputMode="email" autoComplete="email" dir="ltr" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="c-phone">
              {m.contact.phoneLabel}
            </label>
            <input id="c-phone" className="input" type="tel" inputMode="tel" autoComplete="tel" dir="ltr" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>

          {casting && (
            <>
              <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
                <legend className="field__label">{m.contact.languagesLabel}</legend>
                <div className="radio-inline">
                  {CONTACT_LANGUAGES.map((l) => (
                    <label key={l}>
                      <input type="checkbox" checked={languages.includes(l)} onChange={(e) => setLanguages(e.target.checked ? [...languages, l] : languages.filter((x) => x !== l))} />
                      {m.contact.languages[l]}
                    </label>
                  ))}
                </div>
                {languages.includes("other") && (
                  <input className="input" dir="auto" placeholder={m.contact.languagesOtherLabel} aria-label={m.contact.languagesOtherLabel} value={languagesOther} onChange={(e) => setLanguagesOther(e.target.value)} />
                )}
              </fieldset>
              <div className="field">
                <label className="field__label" htmlFor="c-loc">
                  {m.contact.locationLabel}
                </label>
                <input id="c-loc" className="input" dir="auto" autoComplete="address-level2" value={location} onChange={(e) => setLocation(e.target.value)} />
              </div>
              <div className="field">
                <span className="field__label">{m.contact.linksLabel}</span>
                {links.map((l, i) => (
                  <input key={i} className="input" type="url" inputMode="url" dir="ltr" placeholder="https://" aria-label={`${m.contact.linksLabel} ${i + 1}`} value={l} onChange={(e) => setLinks(links.map((x, j) => (j === i ? e.target.value : x)))} />
                ))}
                {links.length < 3 && (
                  <button type="button" className="linkish" onClick={() => setLinks([...links, ""])}>
                    {m.contact.addLink}
                  </button>
                )}
              </div>
              <label className="check">
                <input type="checkbox" checked={ageConfirmed} onChange={(e) => setAgeConfirmed(e.target.checked)} />
                <span>
                  <span className="check__tag">{m.share.required}</span>
                  <br />
                  {m.contact.ageConfirm}
                </span>
              </label>
            </>
          )}

          <div className="field">
            <label className="field__label" htmlFor="c-msg">
              {m.contact.messageLabel}
            </label>
            <textarea id="c-msg" className="textarea" dir="auto" required maxLength={5000} value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>

          <label className="check">
            <input type="checkbox" checked={consentStore} onChange={(e) => setConsentStore(e.target.checked)} />
            <span>
              <span className="check__tag">{m.share.required}</span>
              <br />
              {m.contact.consentStore}
            </span>
          </label>
          {casting && (
            <label className="check">
              <input type="checkbox" checked={keepOnFile} onChange={(e) => setKeepOnFile(e.target.checked)} />
              <span>
                <span className="check__tag check__tag--optional">{m.share.optional}</span>
                <br />
                {m.contact.keepOnFile}
              </span>
            </label>
          )}
          <div className="hp" aria-hidden="true">
            <label>
              Fax
              <input type="text" name="fax" tabIndex={-1} autoComplete="off" defaultValue="" />
            </label>
          </div>
          {error && (
            <p className="field__error" role="alert">
              {error}
            </p>
          )}
          <div className="actions" style={{ justifyContent: "flex-end" }}>
            <button className="btn btn--primary" type="submit" disabled={state === "busy"}>
              {m.contact.submit}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
